/* Movatic WMS — Coletor | Execucao (sequencia guiada REAL, ADR-0034 F4b-6-ii) | CesarFilho 932048016
   ----------------------------------------------------------------------------
   A tela de execucao do coletor: consome o useTaskExecution (le next_step, dispacha por passo,
   avanca). O operador pica com o SCANNER (camara/wedge) -- NUNCA clique-na-imagem. Segue o
   design-system (4 zonas: cabecalho/retrato/dados/acao; laranja --col-alert no tipo de passo;
   verde-agua --col-success EXCLUSIVO no Confirmar e no ✓ conclusao -- decisao 3). Execucao real
   sempre; nada de simulacao (isso e' o Movatic DEV). */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MapPin,
  Package,
  PackageCheck,
  Boxes,
  Wrench,
  Camera,
  Keyboard,
  Ban,
} from 'lucide-react'
import { sanitizeScan } from '@/lib/scan'
import { useTaskOffer } from '../components/TaskOfferProvider'
import { useTaskExecution, execUuid } from '../hooks/useTaskExecution'
import { useKeyboardScanner } from '../hooks/useKeyboardScanner'
import CameraScanner from '../components/CameraScanner'
import { useFeedback } from '../hooks/useFeedback'

const num = (v: unknown, def = 0): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : def
}
const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 })
const fmt = (v: unknown) => nf.format(num(v))

const STEP_LABEL: Record<string, string> = {
  machine: 'Vincular máquina',
  support: 'Vincular suporte',
  location: 'Ir ao endereço',
  pick: 'Picar produto',
  deliver: 'Entregar suporte',
}

export default function Execucao() {
  const { activeTask, clearActive } = useTaskOffer()
  const taskId = (activeTask?.id as number | undefined) ?? null
  const navigate = useNavigate()
  const fb = useFeedback()

  const exec = useTaskExecution(taskId)
  const ns = exec.nextStep
  const step = ns?.current_step
  const line = ns?.current_line ?? null
  const resId = line?.reservation_id ?? null
  const pickMode: 'UNIT' | 'QUANTITY' =
    line?.pick_mode === 'UNIT' ? 'UNIT' : 'QUANTITY'

  const [useCamera, setUseCamera] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)
  const [qtyInput, setQtyInput] = useState('')
  const [scanned, setScanned] = useState(false) // QUANTITY: SKU validado -> revela o campo
  const [faltam, setFaltam] = useState<number | null>(null) // unidades restantes da linha

  // idem_key fixa POR GESTO (ADR-0033 D6): so' regenera apos o backend responder (retry reusa).
  const idemRef = useRef<string | null>(null)
  const idemFor = () => (idemRef.current ??= execUuid())

  // (re)seed ao mudar a linha corrente (reservation_id) ou o passo.
  useEffect(() => {
    setFaltam(line ? num(line.qty) : null)
    setScanned(false)
    setQtyInput('')
    idemRef.current = null
    setFlash(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resId, step])

  // conclusao: passo done -> ✓ verde -> liberta a task ativa e volta a idle (/m).
  useEffect(() => {
    if (step !== 'done') return
    fb.done()
    const t = setTimeout(() => {
      clearActive()
      navigate('/m')
    }, 1400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const remaining = faltam ?? num(line?.qty)
  const recolhidas = num(line?.qty) - remaining

  function reportShort(r: any) {
    const parts: string[] = ['Esgotado declarado — endereço suspeito.']
    if (r?.redirected_lines)
      parts.push(`${r.redirected_lines} linha(s) redirecionada(s).`)
    if (r?.count_task_id)
      parts.push(`Auditoria de inventário #${r.count_task_id}.`)
    if (r?.replen_task_id) parts.push(`Reposição #${r.replen_task_id}.`)
    setFlash(parts.join(' '))
    fb.short()
  }

  const doConfirm = useCallback(
    async (quantity: number) => {
      if (resId == null) return
      const r = await exec.confirmPick({
        reservationId: resId,
        quantity,
        scanCode: line?.product_code,
        idemKey: idemFor(),
      })
      if (r === null) {
        fb.err() // erro de rede/estado -> NAO limpa idemRef (retry reusa a chave)
        return
      }
      idemRef.current = null
      setScanned(false)
      setQtyInput('')
      if (r.short) reportShort(r)
      else {
        setFaltam(num(r.qty_short))
        if (!r.all_picked) fb.ok()
      }
      // r.all_picked -> o refetch do hook traz current_step='done' -> o useEffect conclui.
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exec, resId, line],
  )

  // -- leitura por SCAN (o input do coletor; nunca clique) --
  const onScan = useCallback(
    (raw: string) => {
      if (exec.busy || !step) return
      const code = sanitizeScan(raw)
      if (!code) return
      setFlash(null)
      if (step === 'support') {
        void exec.bindSupport(code)
      } else if (step === 'location') {
        const want = ns?.expected_code
        if (want && code !== want) {
          setFlash(`Leitura "${code}" não é o endereço ${want}.`)
          fb.err()
          return
        }
        void exec.startLocation(code)
      } else if (step === 'pick') {
        if (line?.product_code && code !== line.product_code) {
          setFlash(
            `Leitura "${code}" não corresponde ao produto (${line.product_code}).`,
          )
          fb.err()
          return
        }
        if (pickMode === 'UNIT') void doConfirm(1)
        else setScanned(true) // QUANTITY: o scan valida o SKU e revela o campo
      } else if (step === 'deliver') {
        // F4b-7-ii: entrega do suporte -> pica a zona de cheios (PICK-CHEIOS = expected_code).
        const want = ns?.expected_code
        if (want && code !== want) {
          setFlash(`Leitura "${code}" não é a zona ${want}.`)
          fb.err()
          return
        }
        void exec.deliver(code) // drop_support + finish_task -> next_step='done'
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exec, step, ns, line, pickMode, doConfirm],
  )

  useKeyboardScanner(onScan)

  const submitQty = () => {
    const n = Number(qtyInput)
    if (!Number.isFinite(n) || n < 0) {
      setFlash('Quantidade inválida.')
      fb.err()
      return
    }
    if (n > remaining) {
      // sem clamp: acima do restante e' erro, nao se aceita.
      setFlash(`Não pode exceder o restante (${fmt(remaining)}).`)
      fb.err()
      return
    }
    void doConfirm(n) // n === 0 -> SHORT
  }

  // ---------- estados de moldura ----------
  if (taskId == null) {
    return (
      <Frame>
        <div className="text-sm text-neutral-500">
          Sem tarefa ativa. Aguarda a convocação.
        </div>
      </Frame>
    )
  }
  if (exec.loading || !ns) {
    return (
      <Frame>
        <div className="text-sm text-neutral-500">A carregar o passo…</div>
      </Frame>
    )
  }
  if (step === 'done') {
    return (
      <Frame>
        <div
          className="flex flex-col items-center justify-center gap-3 py-16"
          style={{ color: 'var(--col-success)' }}
        >
          <div
            className="grid place-items-center rounded-full"
            style={{
              width: 72,
              height: 72,
              background: 'var(--bg-success-tile)',
            }}
          >
            <Package size={34} />
          </div>
          <div className="text-xl font-semibold">✓ Tarefa concluída</div>
        </div>
      </Frame>
    )
  }

  // Todos os passos conhecidos, EXPLICITOS -> o fallback nao cai em "maquina" (bug F4b-7-ii):
  // um step desconhecido mostra um estado neutro, nao um passo errado.
  const instrucao =
    step === 'support'
      ? 'Pica um suporte (qualquer suporte válido serve).'
      : step === 'location'
        ? `Pica o endereço ${ns.expected_code ?? ''}`.trim()
        : step === 'pick'
          ? `Pica o produto ${line?.product_code ?? ''}`.trim()
          : step === 'deliver'
            ? `Entrega o suporte na zona de cheios — pica ${ns.expected_code ?? 'PICK-CHEIOS'}`
            : step === 'machine'
              ? 'Vincula a máquina no coletor.'
              : 'Passo desconhecido — recarrega a tarefa.'

  return (
    <Frame>
      {/* Zona 1 — cabeçalho: tarefa + tipo de passo (laranja) + linhas restantes */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">Tarefa #{taskId}</span>
        <div className="flex items-center gap-2">
          {step && STEP_LABEL[step] && (
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{
                background: 'var(--col-alert)',
                color: 'var(--tx-on-alert)',
              }}
            >
              {STEP_LABEL[step]}
            </span>
          )}
          <span className="text-xs text-neutral-500 tabular-nums">
            faltam {ns.remaining_lines}
          </span>
        </div>
      </div>

      {/* Zona 2 — retrato (imagem do certo; se null no pick, sem imagem/placeholder) */}
      <StepPortrait
        step={step}
        image={step === 'pick' ? (line?.product_image ?? null) : null}
      />

      {/* Zona 3 — dados: instrução + (produto) nome + faltam/total */}
      <div className="grid gap-1">
        <div className="text-base font-medium">{instrucao}</div>
        {step === 'pick' && (
          <>
            <div className="text-sm text-neutral-600">
              {String(
                (activeTask as any)?.lines?.find?.(
                  (l: any) => l?.reservation_id === resId,
                )?.product_name ||
                  line?.product_code ||
                  '—',
              )}
            </div>
            <div className="text-sm text-neutral-600">
              Faltam{' '}
              <span className="font-semibold tabular-nums">
                {fmt(remaining)}
              </span>{' '}
              de <span className="tabular-nums">{fmt(line?.qty)}</span>
              {pickMode === 'UNIT' && (
                <span className="ml-2 text-neutral-500">
                  · {fmt(recolhidas)} recolhida(s)
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {flash && <div className="card p-3 text-sm text-amber-700">{flash}</div>}
      {exec.error && (
        <div className="card p-3 text-sm text-amber-700">{exec.error}</div>
      )}

      {/* Zona 4 — ação: leitor + (QUANTITY) campo + (pick) "não há mais aqui" */}
      <div className="grid gap-3">
        <div className="flex items-center gap-2">
          <button
            className="btn"
            onClick={() => setUseCamera((v) => !v)}
            aria-label="Alternar leitor camera/teclado"
          >
            {useCamera ? <Keyboard size={16} /> : <Camera size={16} />}
            <span className="ml-1">
              {useCamera ? 'Usar leitor' : 'Usar câmara'}
            </span>
          </button>
          <span className="text-xs text-neutral-400">
            {useCamera ? 'Aponta a câmara ao código' : 'Leitor físico ativo'}
          </span>
        </div>
        {useCamera && <CameraScanner onRead={onScan} />}

        {/* QUANTITY: campo numérico + Confirmar (verde-água), só após validar o SKU */}
        {step === 'pick' && pickMode === 'QUANTITY' && scanned && (
          <div className="flex items-center gap-2">
            <input
              className="input"
              type="number"
              inputMode="decimal"
              min={0}
              autoFocus
              value={qtyInput}
              onChange={(e) => setQtyInput(e.target.value)}
              placeholder="Quantidade (0 = esgotado)"
              aria-label="Quantidade recolhida"
            />
            <button
              className="btn font-semibold"
              onClick={submitQty}
              disabled={exec.busy}
              style={{
                background: 'var(--col-success)',
                color: 'var(--bg-app)',
              }}
            >
              Confirmar
            </button>
          </div>
        )}

        {/* passo produto: esgotado (SHORT) — neutro/vermelho, nunca verde */}
        {step === 'pick' && (
          <button
            className="btn flex items-center gap-1 text-red-600"
            onClick={() => void doConfirm(0)}
            disabled={exec.busy}
            aria-label="Declarar que não há mais neste endereço"
          >
            <Ban size={16} /> Não há mais aqui
          </button>
        )}
      </div>
    </Frame>
  )
}

function Frame({ children }: { children: React.ReactNode }) {
  return <div className="py-3 grid gap-3">{children}</div>
}

/** Zona 2 (retrato): imagem-do-certo no pick, ou ícone genérico do passo. Placeholder
 *  gradiente só quando o passo NÃO é produto (o produto sem imagem fica sem retrato). */
function StepPortrait({
  step,
  image,
}: {
  step?: string
  image: string | null
}) {
  if (step === 'pick') {
    if (!image) return null // produto sem imagem: sem retrato, sem placeholder (adenda D1)
    return (
      <div
        className="w-full rounded-2xl overflow-hidden grid place-items-center"
        style={{
          height: 150,
          background: 'linear-gradient(135deg, #1a2640, #243556)',
        }}
      >
        <img
          src={image}
          alt=""
          className="w-full h-full object-contain"
          loading="lazy"
        />
      </div>
    )
  }
  const Icon =
    step === 'location'
      ? MapPin
      : step === 'machine'
        ? Wrench
        : step === 'deliver'
          ? PackageCheck
          : Boxes
  return (
    <div
      className="w-full rounded-2xl grid place-items-center"
      style={{
        height: 150,
        background: 'linear-gradient(135deg, #1a2640, #243556)',
      }}
    >
      <Icon size={56} className="text-neutral-400" />
    </div>
  )
}
